import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { creditRatingScalesMapping } from 'shared/mappings';
import { ratingConstants } from 'shared/constants/company';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { endorsementCreditRating } from 'shared/constants/summaryTab';
import { BlurWrapper, Loader, ContactAdvisor } from 'ui';
import { SummaryCard, HeadSquare, SummaryCardLineChart } from 'modules/common/SummaryCard';
import { requestCreditReviewRatings } from 'services';
import { getAuthDataOrganisationId, getAuthDataUserId } from 'selectors/auth';
import summaryTabHistoryData from 'assets/mockedData/summaryTabHistoryData.json';
import RequestContent from '../common/SummaryCard/Request/RequestContent';
import { generalInformationMock } from '../common/SummaryCard/Request/generalInformationMock';

const data = {
  latestChange: {},
  generalInformation: {},
  levels: {},
  dates: {},
  documents: [],
  ratingScalesData: creditRatingScalesMapping(
    portfolioTypes.CREDIT_REVIEW,
    ratingConstants.entityReview,
  ),
  generalInformationList: generalInformationMock,
  endorsementList: endorsementCreditRating,
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
  },
  content: {
    flexDirection: 'column',
    alignContent: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  contentRow: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const useHeadSquareStyles = makeStyles({
  score: {
    fontWeight: 500,
    fontSize: 60,
  },
  max: {
    fontSize: 24,
  },
  scoreWidgetMainColumn: {
    height: '100%',
  },
});

const SummaryCardRequestCreditReview = ({
  className,
  easyNumber,
  onRequest,
  isAvailable,
  wasRequested,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const headSquareClasses = useHeadSquareStyles();
  const organisationId = useSelector(getAuthDataOrganisationId);
  const userId = useSelector(getAuthDataUserId);
  const [requested, setRequested] = useState(wasRequested);
  const [isRequestRatingPending, setIsRequestRatingPending] = useState(false);

  const handleRequest = async () => {
    setIsRequestRatingPending(true);

    const response = await requestCreditReviewRatings({
      ids: [easyNumber],
      userId,
      organisationId,
    });

    if (response.success) {
      setRequested(true);
      onRequest();
    }
  };

  let showRatingIsNotAvailable = false;

  if (!easyNumber || !isAvailable) {
    showRatingIsNotAvailable = true;
  }

  const content = !showRatingIsNotAvailable ? (
    <RequestContent onRequest={handleRequest} isPending={requested}>
      <Grid className={classes.contentRow}>
        <Typography display="block">
          {t('If you wish to see the review for this company, please request it here.')}
        </Typography>
        <Typography display="block">
          {t(
            'If it is available and covered by your subscription, you will find it here immediately.',
          )}
        </Typography>
      </Grid>
    </RequestContent>
  ) : (
    <Grid className={classes.contentRow}>
      <ContactAdvisor>
        <Typography display="block">{t('Coming soon')}</Typography>
        <Typography display="block">
          <strong>{t('Credit Review. ')}</strong>
          {t('The primary')} <strong>{t('Early Warning System')}</strong>{' '}
          {t('for companies’ credit quality.')}
        </Typography>
        <Typography display="block">
          {t(
            'If you would like to know more about this service, please contact your personal advisor.',
          )}
        </Typography>
      </ContactAdvisor>
    </Grid>
  );

  return (
    <BlurWrapper
      className={className}
      content={
        <Grid container className={classes.content}>
          {isRequestRatingPending ? <Loader center /> : content}
        </Grid>
      }
      active
    >
      <SummaryCard
        headSquare={
          <HeadSquare title={t('Credit Review assessment')}>
            <Box>
              <span className={headSquareClasses.score}>8</span>
              <span className={headSquareClasses.max}>/10</span>
            </Box>
            <Typography>{t('PD')} : 2%</Typography>
          </HeadSquare>
        }
        history={<SummaryCardLineChart history={summaryTabHistoryData} />}
        data={data}
      />
    </BlurWrapper>
  );
};

SummaryCardRequestCreditReview.defaultProps = {
  className: undefined,
  easyNumber: undefined,
  wasRequested: undefined,
  onRequest: undefined,
};

SummaryCardRequestCreditReview.propTypes = {
  isAvailable: PropTypes.bool.isRequired,
  wasRequested: PropTypes.bool,
  onRequest: PropTypes.func,
  easyNumber: PropTypes.string,
  className: PropTypes.string,
};

export default SummaryCardRequestCreditReview;
